import { Camera, X, RotateCcw, Scan, FileText, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { labelUdm } from "../../utils/udmUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import IngredienteCombobox from "./IngredienteCombobox";
import { ImagePreview } from "./ImagePreview";
import { fileUrl, thumbUrl } from "../../lib/fileUrl";
import { LottoDateFields } from "./LottoDateFields";
import { PosizioneFrigoSelect } from "./PosizioneFrigoSelect";

const resolveImgSrc = (path) => fileUrl(path);

const isDataPassata = (value) => {
  if (!value) return false;
  const scadenza = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(scadenza.getTime())) return false;
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  return scadenza < oggi;
};

export const ScanDialog = ({
  open, onOpenChange, form, setForm, capturedImages, setCapturedImages,
  fileInputRef, onAddPhoto, onRunOCR, ocrLoading, onSave,
  ingredienti, categorie, onCreateIngrediente,
  onOpenFattura, onRemoveFattura, fatturaPreviewUrl,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-border p-0">
      <div className="p-5 pb-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-foreground">
            <Camera className="h-5 w-5 text-lime-500" />
            Nuovo Lotto Ingrediente
          </DialogTitle>
          <DialogDescription className="sr-only">Dati del lotto ingrediente da registrare.</DialogDescription>
        </DialogHeader>
      </div>

      <div className="p-5 space-y-4">
        <div>
          {capturedImages.length > 0 ? (
            <div className="space-y-2">
              <div className="relative">
                <img src={capturedImages[0].preview} alt="Foto ingrediente" className="w-full aspect-[4/3] object-cover rounded-2xl border-2 border-lime-500/30" />
                <button onClick={() => setCapturedImages([])} className="absolute top-2 right-2 w-8 h-8 bg-black/80 text-white rounded-full flex items-center justify-center shadow-lg" data-testid="remove-scan-photo">
                  <X className="h-4 w-4" />
                </button>
                <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-2 right-2 px-3 py-1.5 bg-black/80 text-white rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                  <RotateCcw className="h-3 w-3" /> Rifare
                </button>
              </div>
              {ocrLoading && (
                <div className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600/20 text-blue-400 font-bold text-sm">
                  <Scan className="h-4 w-4 animate-pulse" /> Lettura in corso...
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => fileInputRef.current?.click()} data-testid="scan-photo-btn"
              className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed border-border bg-muted/50 flex flex-col items-center justify-center gap-3 active:scale-[0.98] transition-all">
              <div className="w-14 h-14 rounded-full bg-lime-500/10 flex items-center justify-center">
                <Camera className="h-7 w-7 text-lime-500" />
              </div>
              <div className="text-center">
                <p className="font-medium text-sm">Scatta foto</p>
                <p className="text-xs text-muted-foreground">Fotografa l'ingrediente</p>
              </div>
            </button>
          )}
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Ingrediente *</Label>
            <IngredienteCombobox ingredienti={ingredienti} categorie={categorie} value={form.ingrediente_id}
              onSelect={(id) => setForm({ ...form, ingrediente_id: id })} onCreateNew={onCreateIngrediente} testId="scan-ingrediente-combobox" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">N. Lotto <span className="text-rose-500">*</span></Label>
              <Input value={form.numero_lotto} onChange={(e) => setForm({ ...form, numero_lotto: e.target.value })} placeholder="Es. L12345" className="h-11 rounded-xl" data-testid="scan-numero-lotto" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Scadenza</Label>
              <DateInput
                value={form.data_scadenza}
                onChange={(v) => setForm({ ...form, data_scadenza: v })}
                disablePast
                testId="scan-scadenza"
                pastErrorMessage="La scadenza rilevata è già passata. Seleziona oggi o una data futura."
              />
              {isDataPassata(form.data_scadenza) && (
                <p className="mt-1 text-xs font-semibold text-red-600" data-testid="scan-expired-date-error">
                  La scadenza rilevata è già passata. Correggila prima di salvare.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Data creazione</Label>
              <Input type="date" value={form.data_apertura} readOnly className="h-11 rounded-xl bg-muted/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Operatore</Label>
              <Input value={form.operatore} readOnly className="h-11 rounded-xl bg-muted/50" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Quantità</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.001" value={form.quantita_preparata}
                onChange={(e) => setForm({ ...form, quantita_preparata: e.target.value })}
                placeholder="Es. 3" className="h-11 rounded-xl" data-testid="scan-quantita" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-foreground uppercase tracking-wide">Unità di misura</Label>
              <select value={form.unita_misura || "kg"}
                onChange={(e) => setForm({ ...form, unita_misura: e.target.value })}
                className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
                data-testid="scan-udm">
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
                <option value="pz">pz</option>
              </select>
            </div>
          </div>
        </div>

        {/* Carica Fattura (facoltativo) — SOPRA il tasto Salva lotto */}
        {onOpenFattura && (
          form.fattura_immagine_path ? (
            <div
              className="w-full flex items-center gap-3 p-2 rounded-xl border border-indigo-200 bg-indigo-50/60"
              data-testid="fattura-selected-preview"
            >
              <img
                src={fatturaPreviewUrl || thumbUrl(form.fattura_immagine_path)}
                alt="Fattura selezionata"
                data-testid="scan-fattura-thumbnail"
                className="w-14 h-14 object-cover rounded-lg border border-indigo-200 flex-shrink-0"
                onError={(e) => { if (!fatturaPreviewUrl && !e.currentTarget.dataset.full) { e.currentTarget.dataset.full = "1"; e.currentTarget.src = resolveImgSrc(form.fattura_immagine_path); } }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-indigo-700 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Fattura allegata
                </p>
                <ImagePreview path={form.fattura_immagine_path} previewUrl={fatturaPreviewUrl} label="Vedi anteprima" className="mt-1" />
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={onOpenFattura}
                  className="text-[11px] font-bold text-indigo-700 hover:underline"
                  data-testid="fattura-change-btn"
                >
                  Cambia
                </button>
                {onRemoveFattura && (
                  <button
                    type="button"
                    onClick={onRemoveFattura}
                    className="text-[11px] font-bold text-red-600 hover:underline"
                    data-testid="fattura-remove-btn"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onOpenFattura}
              className="w-full h-12 rounded-xl text-base font-semibold flex items-center justify-center gap-2 border-2 border-dashed border-indigo-300 bg-indigo-50/40 text-indigo-700 hover:bg-indigo-50 transition-colors"
              data-testid="carica-fattura-btn"
            >
              <FileText className="h-5 w-5" />
              <span>Carica Fattura</span>
              <span className="text-xs font-normal text-indigo-500/80">(facoltativo)</span>
            </button>
          )
        )}

        <button
          className={`w-full h-12 rounded-xl text-base font-bold transition-colors ${!form.ingrediente_id || ocrLoading || isDataPassata(form.data_scadenza) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-lime-500 hover:bg-lime-600'}`}
          style={(!form.ingrediente_id || ocrLoading || isDataPassata(form.data_scadenza)) ? { backgroundColor: '#d1d5db', color: '#9ca3af' } : {}}
          onClick={onSave} disabled={!form.ingrediente_id || ocrLoading || isDataPassata(form.data_scadenza)} data-testid="salva-lotto-ingrediente">
          {ocrLoading ? "Salvataggio..." : "Salva lotto"}
        </button>      </div>
    </DialogContent>
  </Dialog>
);

export const EtichettaDialog = ({
  open, onOpenChange, editItem, form, setForm, onSubmit,
  ingredienti, categorie, posizioni, frigoriferi, onCreateIngrediente,
}) => {
  const hasIngredienti = ingredienti && ingredienti.length > 0;
  const isComposto = form.is_composto;
  const prodottoNome = editItem?.nome_preparazione || editItem?.nome_prodotto || "";
  const hasNumericQuantity = editItem?.quantita_preparata !== null && editItem?.quantita_preparata !== undefined;

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="etichetta-dialog">
      <DialogHeader>
        <DialogTitle className="text-lg">{editItem ? "Modifica Etichetta" : "Nuova Etichetta HACCP"}</DialogTitle>
        <DialogDescription className="sr-only">Dati dell'etichetta HACCP.</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-5">
        <div className="space-y-2">
          {editItem ? (
            <>
              <Label className="text-sm font-semibold text-zinc-700">{isComposto ? "Preparazione" : "Ingrediente"}</Label>
              <Input value={prodottoNome || ingredienti?.find(i => i.id === form.ingrediente_id)?.nome || form.nome_prodotto || "—"} readOnly className="h-11 bg-muted/50" />
            </>
          ) : isComposto ? (
            <>
              <Label className="text-sm font-semibold text-zinc-700">Preparazione</Label>
              <Input value={prodottoNome} readOnly className="h-11 bg-muted/50" />
            </>
          ) : hasIngredienti ? (
            <>
              <Label className="text-sm font-semibold text-zinc-700">Ingrediente *</Label>
              <IngredienteCombobox ingredienti={ingredienti} categorie={categorie} value={form.ingrediente_id}
                onSelect={(id) => setForm({ ...form, ingrediente_id: id, nome_prodotto: "" })} onCreateNew={onCreateIngrediente} testId="manual-ingrediente-combobox" />
            </>
          ) : (
            <>
              <Label className="text-sm font-semibold text-zinc-700">Nome Prodotto *</Label>
              <Input value={form.nome_prodotto || ""} onChange={(e) => setForm({ ...form, nome_prodotto: e.target.value })}
                placeholder="Es. Mozzarella, Pomodori pelati..." data-testid="manual-nome-prodotto" autoFocus />
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Numero Lotto <span className="text-rose-500">*</span></Label>
            <Input value={form.numero_lotto} onChange={(e) => setForm({ ...form, numero_lotto: e.target.value })} placeholder="Es. L12345" />
          </div>
          <div className="space-y-2">
            {(() => {
              const ing = ingredienti?.find(i => i.id === form.ingrediente_id);
              const udm = (hasNumericQuantity && editItem?.unita_misura) || ing?.unita_misura;
              return (
                <>
                  <Label className="flex items-center gap-1.5">
                    Quantità
                    {udm && <span data-testid="etichetta-quantita-unit" className="text-xs px-1.5 py-0.5 rounded font-bold" style={{ background: "rgba(108,99,255,0.10)", color: "#6C63FF" }}>{labelUdm(udm)}</span>}
                  </Label>
                  {hasNumericQuantity ? (
                    <Input type="text" inputMode="decimal" pattern="[0-9]+([.,][0-9]+)?" required
                      value={form.quantita_preparata ?? ""}
                      onChange={e => setForm(prev => ({ ...prev, quantita_preparata: e.target.value }))}
                      data-testid="etichetta-quantita" aria-label={`Quantità (${editItem.unita_misura || udm || "unità"})`} />
                  ) : (
                    <Input value={form.quantita_testo ?? ""} onChange={e => setForm(prev => ({ ...prev, quantita_testo: e.target.value }))}
                      placeholder={udm ? `Es. 750 ${labelUdm(udm)}` : "Es. 750ml, 1kg"} data-testid="etichetta-quantita" />
                  )}
                </>
              );
            })()}
          </div>
        </div>

        <LottoDateFields form={form} setForm={setForm} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PosizioneFrigoSelect value={form.posizione} onChange={v => setForm(prev => ({ ...prev, posizione: v }))}
            frigoriferi={frigoriferi} posizioniLegacy={posizioni} testId="etichetta-posizione" />
          <div className="space-y-2">
            <Label>Operatore</Label>
            <Input value={form.operatore} onChange={(e) => setForm({ ...form, operatore: e.target.value })} placeholder="Nome operatore" data-testid="etichetta-operatore" />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="etichetta-annulla">Annulla</Button>
          <Button type="submit" data-testid="etichetta-salva">{editItem ? "Salva" : "Crea Etichetta"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
  );
};

export const NuovoIngredienteDialog = ({
  open, onOpenChange, form, setForm, onSubmit, categorie
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Nuovo Ingrediente</DialogTitle>
          <DialogDescription className="sr-only">Dati del nuovo ingrediente.</DialogDescription>
        </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Nome Ingrediente *</Label>
          <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome dell'ingrediente" autoFocus />
        </div>
        <div className="space-y-2">
          <Label>Categoria (opzionale)</Label>
          <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
            <SelectContent>{categorie.map((c) => (<SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Unità di Misura</Label>
          <Select value={form.unita_misura} onValueChange={(v) => setForm({ ...form, unita_misura: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="kg">kg</SelectItem>
              <SelectItem value="g">g</SelectItem>
              <SelectItem value="litri">litri (L)</SelectItem>
              <SelectItem value="ml">ml</SelectItem>
              <SelectItem value="pz">pz</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button type="submit">Crea</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
